import { useEffect, useState } from "react";
import AuthLayout from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff, Loader } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/lib/supabase";
import { useRouter } from "@/routes/hooks";
import { useAuthContext } from "@/hooks/use-auth-context";
import useNoIndex from "@/routes/hooks/use-noindex";
import { Helmet } from "react-helmet-async";
import { toast } from "@/hooks/use-toast";
import { CONFIG } from "@/config-global";
import {
  getInviteEmailFromSearch,
  readPendingInviteAuth,
  savePendingInviteAuth,
} from "@/utils/invite-auth-flow";
import { isLinkedInOnlyEmail, startLinkedInSignIn } from "@/utils/linkedin-sign-in";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(1, "Enter your password"),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  useNoIndex();
  const router = useRouter();
  const { checkUserSession } = useAuthContext();
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Read the browser URL directly so an invite email survives the GuestGuard
  // handoff even if the router's search-param snapshot was created earlier.
  const invitedEmail = getInviteEmailFromSearch(window.location.search);

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: invitedEmail, password: "" },
    mode: "onChange",
  });

  // A LinkedIn address signs in with LinkedIn only: the password, reset and
  // Google options disappear as soon as one is typed. See linkedin-sso-routing.ts.
  const email = form.watch("email");
  const linkedInOnly = isLinkedInOnlyEmail(email);

  useEffect(() => {
    if (invitedEmail) {
      form.setValue("email", invitedEmail, { shouldValidate: true });
    }
  }, [form, invitedEmail]);

  async function onSubmit(values: LoginForm) {
    // A password autofilled before the address finished rendering can still
    // submit here.
    if (isLinkedInOnlyEmail(values.email)) {
      await signInWithLinkedIn();
      return;
    }
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });
      if (error) throw error;
      await checkUserSession?.();
      try {
        const inviteToken = sessionStorage.getItem("pending-invite-token");
        if (inviteToken) {
          router.push(`/invite?token=${encodeURIComponent(inviteToken)}`);
          return;
        }
      } catch {
        // Ignore sessionStorage errors
      }
      router.push("/auth/post-auth");
    } catch (err: any) {
      toast({ variant: "destructive", title: "Sign in failed", description: err?.message ?? "Please try again." });
    } finally {
      setIsLoading(false);
    }
  }

  async function signInWithGoogle() {
    try {
      setIsLoading(true);
      try {
        const pendingInvite = readPendingInviteAuth(sessionStorage);
        if (pendingInvite?.token && (pendingInvite.email || invitedEmail)) {
          savePendingInviteAuth(sessionStorage, {
            token: pendingInvite.token,
            email: pendingInvite.email || invitedEmail,
            method: "google",
          });
        }
      } catch {
        // Ignore sessionStorage errors; PostAuth will still validate normal access.
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: window.location.origin + "/auth/post-auth" },
      });
      if (error) throw error;
    } catch (err: any) {
      toast({ variant: "destructive", title: "Google sign-in failed", description: err?.message ?? "Please try again." });
    } finally {
      setIsLoading(false);
    }
  }

  async function signInWithLinkedIn() {
    try {
      setIsLoading(true);
      // Only a LinkedIn address is worth pre-filling at Okta.
      const typedEmail = form.getValues("email");
      await startLinkedInSignIn(isLinkedInOnlyEmail(typedEmail) ? typedEmail : undefined);
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "LinkedIn sign-in failed",
        description: err?.message ?? "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
    <Helmet>
      <title>Sign in — Bizly Router</title>
      <meta name="description" content="Sign in to Bizly Router to access your assistant and events." />
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <AuthLayout>
      <section className="flex flex-col items-center">
        <div className="w-full max-w-[549px] rounded-3xl border border-border-strong bg-surface-base">
          <div className="px-8 sm:px-10 py-10">
            <div className="flex flex-col items-center gap-3">
              <h1 className="text-[28px] font-semibold tracking-tight text-text-primary text-center">
                Welcome back
              </h1>
              <p className="text-sm font-medium text-text-muted text-center">
                Login using your email or other social links.
              </p>
            </div>

            <form
              onSubmit={
                linkedInOnly
                  ? (event) => {
                      event.preventDefault();
                      void signInWithLinkedIn();
                    }
                  : form.handleSubmit(onSubmit)
              }
              className="mt-11 space-y-10"
            >
              <div className="space-y-3">
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="Email address"
                  defaultValue={invitedEmail}
                  className="w-full h-12 rounded-xl border border-border-input bg-surface-base px-4 text-sm font-medium placeholder:text-text-placeholder outline-none focus:border-border-focus transition-colors"
                  {...form.register("email")}
                />

                {linkedInOnly ? (
                  <p role="status" className="px-1 text-sm font-medium text-text-muted">
                    LinkedIn accounts sign in with LinkedIn. You'll continue to LinkedIn's sign-in page.
                  </p>
                ) : (
                <div className="flex h-12 items-center justify-between rounded-xl border border-border-input bg-surface-base px-4">
                  <input
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Password"
                    className="w-full bg-transparent text-sm font-medium placeholder:text-text-placeholder outline-none"
                    {...form.register("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="ml-2 inline-flex h-5 w-5 items-center justify-center text-text-secondary hover:text-text-primary focus:outline-none"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </div>
                )}
              </div>

              {linkedInOnly ? null : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember"
                    checked={remember}
                    onCheckedChange={(v) => setRemember(Boolean(v))}
                    className="h-5 w-5 rounded-[4px] border-text-primary data-[state=checked]:bg-accent-primary data-[state=checked]:text-text-inverse"
                  />
                  <label htmlFor="remember" className="text-sm font-medium text-text-muted">
                    Remember me
                  </label>
                </div>
                <a href={`/forgot-password?email=${encodeURIComponent(form.watch("email"))}`} className="text-sm font-semibold text-text-primary hover:opacity-80">
                  Set or reset password
                </a>
              </div>
              )}

              <div className="space-y-6">
                {linkedInOnly ? (
                  <Button
                    type="submit"
                    className="w-full h-12 bg-accent-primary text-text-inverse hover:bg-accent-primary-hover rounded-xl text-base"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Redirecting...
                      </>
                    ) : (
                      "Continue with LinkedIn"
                    )}
                  </Button>
                ) : (
                <>
                <Button
                  type="submit"
                  className="w-full h-12 bg-accent-primary text-text-inverse hover:bg-accent-primary-hover rounded-xl text-base"
                  disabled={isLoading || !form.formState.isValid}
                >
                  {isLoading ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>

                <div className="flex items-center gap-1">
                  <div className="h-px flex-1 bg-border-strong"></div>
                  <span className="px-3 text-sm font-medium text-text-muted">Or continue with</span>
                  <div className="h-px flex-1 bg-border-strong"></div>
                </div>

                <button
                  type="button"
                  onClick={signInWithGoogle}
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-between rounded-xl bg-neutral-200 px-3 py-3 hover:outline hover:outline-1 hover:outline-border-focus transition-colors"
                >
                  {isLoading ? (
                    <Loader className="h-5 w-5 text-text-secondary animate-spin" />
                  ) : (
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 48 48"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M43.611 20.083H42V20H24v8h11.3C33.893 31.657 29.36 35 24 35c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.06 0 5.84 1.153 7.95 3.05l5.657-5.657C34.53 5.053 29.51 3 24 3 11.85 3 2 12.85 2 25s9.85 22 22 22c11.579 0 21-8.421 21-20 0-1.34-.138-2.651-.389-3.917Z"
                        fill="#FFC107"
                      />
                      <path
                        d="M6.306 14.691l6.571 4.819C14.543 16.23 18.925 13 24 13c3.06 0 5.84 1.153 7.95 3.05l5.657-5.657C34.53 5.053 29.51 3 24 3 16.318 3 9.652 7.337 6.306 14.691Z"
                        fill="#FF3D00"
                      />
                      <path
                        d="M24 47c5.304 0 10.167-1.986 13.853-5.247l-6.392-5.405C29.452 37.792 26.873 39 24 39c-5.34 0-9.853-3.317-11.655-7.966l-6.53 5.028C9.105 42.62 16.022 47 24 47Z"
                        fill="#4CAF50"
                      />
                      <path
                        d="M43.611 20.083H42V20H24v8h11.3c-1.099 3.249-3.535 5.816-6.837 7.189l.006.004 6.392 5.405C34.422 42.93 46 37 46 25c0-1.34-.138-2.651-.389-3.917Z"
                        fill="#1976D2"
                      />
                    </svg>
                  )}
                  <span className="mx-auto text-base font-medium text-text-secondary">{isLoading ? "Redirecting..." : "Continue with Google"}</span>
                </button>

                {CONFIG.auth.linkedInEnabled ? (
                  <button
                    type="button"
                    onClick={signInWithLinkedIn}
                    disabled={isLoading}
                    className="mt-3 w-full inline-flex items-center justify-between rounded-xl bg-neutral-200 px-3 py-3 hover:outline hover:outline-1 hover:outline-border-focus transition-colors"
                  >
                    {isLoading ? (
                      <Loader className="h-5 w-5 text-text-secondary animate-spin" />
                    ) : (
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#0A66C2" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14ZM7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0Z" />
                      </svg>
                    )}
                    <span className="mx-auto text-base font-medium text-text-secondary">
                      {isLoading ? "Redirecting..." : "Continue with LinkedIn"}
                    </span>
                  </button>
                ) : null}
                </>
                )}
              </div>
            </form>
          </div>
        </div>

        <p className="mt-10 max-w-[34ch] text-sm font-medium text-text-muted text-center">
          Access is by invitation only. Ask your Bizly Router admin to invite you with the email address you plan to use.
        </p>
      </section>
    </AuthLayout>
    </>
  );
}
